type HubAccount = {
  id?: string;
  account_id?: string;
  hubPublished?: boolean;
  published_at?: string | null;
  noResponseMode?: string | null;
  publishMode?: string | null;
};

type PublishedOverrideAssessment = {
  id?: string;
  assessment_id?: string;
  published_at?: string | null;
  completed_at?: string | null;
  created_at?: string | null;
  accounts?: HubAccount[];
};

const NO_RESPONSE_NON_LEVEL_MODES = new Set(["retain_previous", "unknown"]);

const parsePublishMs = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
};

const getHubAccountId = (account: HubAccount): string | null =>
  account.id ?? account.account_id ?? null;

/** Matches backend `is_no_response_non_level_publish` for cadence / latest publish. */
export const isNoResponseNonLevelHubPublish = (
  account: HubAccount
): boolean => {
  const mode = (account.noResponseMode ?? account.publishMode ?? "")
    .trim()
    .toLowerCase();
  return NO_RESPONSE_NON_LEVEL_MODES.has(mode);
};

/**
 * Published tab: one Override target per account within a Team or Formal scope.
 * Keys are `${assessmentId}::${accountId}`.
 */
export const buildPublishedOverrideEligibleKeys = (
  assessments: PublishedOverrideAssessment[]
): Set<string> => {
  const bestByAccount = new Map<
    string,
    { key: string; ts: number; assessmentId: string }
  >();

  for (const assessment of assessments) {
    const assessmentId =
      assessment.id || (assessment as { assessment_id?: string }).assessment_id;
    if (!assessmentId) continue;

    const assessmentFallbackTs =
      parsePublishMs(assessment.published_at) ??
      parsePublishMs(assessment.completed_at) ??
      parsePublishMs(assessment.created_at);

    for (const account of assessment.accounts ?? []) {
      if (!account.hubPublished) continue;
      if (isNoResponseNonLevelHubPublish(account)) continue;

      const accountId = getHubAccountId(account);
      if (!accountId) continue;

      const ts = parsePublishMs(account.published_at) ?? assessmentFallbackTs;
      if (ts == null) continue;

      const key = `${assessmentId}::${accountId}`;
      const prev = bestByAccount.get(accountId);
      if (
        !prev ||
        ts > prev.ts ||
        (ts === prev.ts && assessmentId.localeCompare(prev.assessmentId) > 0)
      ) {
        bestByAccount.set(accountId, { key, ts, assessmentId });
      }
    }
  }

  return new Set(Array.from(bestByAccount.values(), entry => entry.key));
};

export const isPublishedOverrideEligibleRow = (
  eligibleKeys: Set<string> | undefined,
  assessmentId: string | undefined,
  accountId: string | null | undefined
): boolean => {
  if (!eligibleKeys) return true;
  if (!assessmentId || !accountId) return false;
  return eligibleKeys.has(`${assessmentId}::${accountId}`);
};
